import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Logo } from '../components/Logo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Progress } from '../components/ui/progress';
import { useAuth } from '../context/AuthContext';
import { 
  ArrowLeft, 
  ArrowRight, 
  Save, 
  Send, 
  User, 
  FileText, 
  Truck, 
  Award,
  Upload,
  X,
  AlertCircle,
  CheckCircle,
  Loader2,
  MapPin,
  Camera
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { id: 1, title: 'Personal Info', icon: User },
  { id: 2, title: 'Identification', icon: FileText },
  { id: 3, title: 'Equipment', icon: Truck },
  { id: 4, title: 'Experience', icon: Award }
];

const stepTone = (isActive, isComplete) => {
  if (isActive) return { text: 'text-violet-400', bg: 'bg-violet-500/20' };
  if (isComplete) return { text: 'text-emerald-400', bg: 'bg-emerald-500/20' };
  return { text: 'text-slate-500', bg: 'bg-slate-800' };
};

const initialFormData = {
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  selected_market: '',
  social_security: '',
  driver_license: '',
  work_experience: '',
  has_truck: false,
  truck_details: '',
  needs_rent_vehicle: false,
  has_tools: false,
  tools_details: '',
  has_meter: false,
  needs_rent_meter: false,
  meter_option: '',
  meter_details: '',
  certifications: '',
  is_us_citizen: '',
  has_criminal_record: ''
};

export default function ApplicationForm() {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState(initialFormData);
  const [fieldErrors, setFieldErrors] = useState({});
  const [application, setApplication] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [markets, setMarkets] = useState([]);
  const [accepted, setAccepted] = useState(false);
  const fileInputRef = useRef(null);
  const ssnFileRef = useRef(null);
  const vehicleFileRef = useRef(null);
  const toolsFileRef = useRef(null);
  const { user, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const userFirstName = user?.first_name;
  const userLastName = user?.last_name;
  const userEmail = user?.email;

  useEffect(() => {
    const fetchMarkets = async () => {
      try {
        const res = await axios.get(`${API_URL}/markets`);
        setMarkets(res.data || []);
      } catch { toast.error('Could not load markets. Please refresh the page.'); }
    };
    fetchMarkets();
  }, []);

  useEffect(() => {
    // Auto-save to localStorage
    localStorage.setItem('mighty-link-application', JSON.stringify({ formData, currentStep }));
  }, [formData, currentStep]);

  const fetchApplication = useCallback(async () => {
    const u = { first_name: userFirstName, last_name: userLastName, email: userEmail };
    try {
      const response = await axios.get(`${API_URL}/applications/mine`, { headers: getAuthHeaders() });
      if (response.data) {
        setApplication(response.data);
        const data = { ...initialFormData, ...(response.data.data || {}) };
        // Pre-fill name from user account if empty
        if (!data.first_name && u.first_name) data.first_name = u.first_name;
        if (!data.last_name && u.last_name) data.last_name = u.last_name;
        if (!data.email && u.email) data.email = u.email;
        setFormData(data);
        setFieldErrors(response.data.field_errors || {});
        setDocuments(response.data.documents || []);
        setCurrentStep(response.data.step || 1);
      } else {
        const saved = localStorage.getItem('mighty-link-application');
        if (saved) {
          const { formData: savedData, currentStep: savedStep } = JSON.parse(saved);
          const merged = { ...initialFormData, ...savedData };
          if (!merged.first_name && u.first_name) merged.first_name = u.first_name;
          if (!merged.last_name && u.last_name) merged.last_name = u.last_name;
          if (!merged.email && u.email) merged.email = u.email;
          setFormData(merged);
          setCurrentStep(savedStep || 1);
        } else {
          // Brand new application - pre-fill from user account
          setFormData({
            ...initialFormData,
            first_name: u.first_name || '',
            last_name: u.last_name || '',
            email: u.email || ''
          });
        }
      }
    } catch (error) {
      const saved = localStorage.getItem('mighty-link-application');
      if (saved) {
        const { formData: savedData, currentStep: savedStep } = JSON.parse(saved);
        const merged = { ...initialFormData, ...savedData };
        if (!merged.first_name && u.first_name) merged.first_name = u.first_name;
        if (!merged.last_name && u.last_name) merged.last_name = u.last_name;
        if (!merged.email && u.email) merged.email = u.email;
        setFormData(merged);
        setCurrentStep(savedStep || 1);
      } else {
        setFormData({
          ...initialFormData,
          first_name: u.first_name || '',
          last_name: u.last_name || '',
          email: u.email || ''
        });
      }
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, userFirstName, userLastName, userEmail]);

  useEffect(() => {
    fetchApplication();
  }, [fetchApplication]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear field error when user edits
    if (fieldErrors[field]) {
      setFieldErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const saveProgress = async (isDraft = true) => {
    setSaving(true);
    try {
      const payload = { data: formData, step: currentStep, is_draft: isDraft };
      
      if (application) {
        await axios.put(`${API_URL}/applications/mine`, payload, { headers: getAuthHeaders() });
      } else {
        const response = await axios.post(`${API_URL}/applications`, payload, { headers: getAuthHeaders() });
        setApplication(response.data);
      }
      
      toast.success('Progress saved!');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save progress');
    } finally {
      setSaving(false);
    }
  };

  const submitApplication = async () => {
    setSubmitting(true);
    try {
      // First save current data
      const payload = { data: formData, step: 4, is_draft: false };
      
      if (application) {
        await axios.put(`${API_URL}/applications/mine`, payload, { headers: getAuthHeaders() });
      } else {
        await axios.post(`${API_URL}/applications`, payload, { headers: getAuthHeaders() });
      }
      
      await axios.post(`${API_URL}/applications/mine/submit`, {}, { headers: getAuthHeaders() });
      
      localStorage.removeItem('mighty-link-application');
      toast.success('Application submitted successfully!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit application');
    } finally {
      setSubmitting(false);
    }
  };

  const handleFileUpload = async (e, documentType) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Save progress first if no application exists
    if (!application) {
      await saveProgress(true);
    }

    const formDataUpload = new FormData();
    formDataUpload.append('file', file);
    formDataUpload.append('document_type', documentType);

    try {
      const response = await axios.post(
        `${API_URL}/applications/mine/documents`,
        formDataUpload,
        { 
          headers: { 
            ...getAuthHeaders(),
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      setDocuments(prev => [...prev, response.data]);
      toast.success('Document uploaded successfully!');
    } catch (error) {
      toast.error('Failed to upload document');
    }
  };

  const getProgress = () => {
    return Math.round((currentStep / STEPS.length) * 100);
  };

  const hasDocType = (type) => documents.some(d => d.document_type === type);

  const canProceedFromStep2 = () => {
    return hasDocType('Driver License') && hasDocType('Social Security');
  };

  const renderFieldError = (field) => {
    if (!fieldErrors[field]) return null;
    return (
      <div className="flex items-center gap-2 mt-1 text-red-400 text-sm" data-testid={`error-${field}`}>
        <AlertCircle size={14} />
        <span>{fieldErrors[field]}</span>
      </div>
    );
  };

  const getInputClassName = (field) => {
    const base = "h-12 bg-[#0A0A0F] border-slate-700 text-white placeholder:text-slate-500 focus:border-violet-500 focus:ring-violet-500/20";
    return fieldErrors[field] ? `${base} border-red-500 bg-red-500/10` : base;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0F] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-violet-500"></div>
      </div>
    );
  }

  if (!accepted) {
    return (
      <div className="min-h-screen bg-[#0A0A0F] flex items-center justify-center px-4 py-8">
        <div className="max-w-2xl w-full bg-[#12121A] rounded-2xl border border-slate-800 p-8 sm:p-10">
          <div className="text-center mb-6">
            <div className="w-16 h-16 rounded-full bg-violet-500/10 border border-violet-500/30 flex items-center justify-center mx-auto mb-6">
              <AlertCircle size={32} className="text-violet-400" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>
              Before You Begin
            </h2>
            <p className="text-slate-400 leading-relaxed">
              Before proceeding with the application, please ensure that you are genuinely interested in joining our company. Recruitment processes require significant time and resources, so we kindly ask that you apply only if you are seriously considering the opportunity.
            </p>
          </div>

          <div className="bg-[#0A0A0F] rounded-xl border border-slate-700 p-5 sm:p-6 mb-8">
            <h3 className="text-sm font-semibold text-violet-400 uppercase tracking-wide mb-4">
              Application & Hiring Process
            </h3>
            <ol className="space-y-3 text-sm text-slate-300 leading-relaxed list-decimal list-inside">
              <li><strong className="text-white">Complete Your Application</strong> — Fill out the Mighty Link application form. It is important to answer all questions thoroughly for a more effective evaluation.</li>
              <li><strong className="text-white">Visual Interview</strong> — A recruitment agent will review your application to determine whether you meet the requirements. Once you pass this visual interview stage, you will receive an email regarding your hiring.</li>
              <li><strong className="text-white">Background Check Authorization</strong> — The email will include an authorization form for you to consent to a background check.</li>
              <li><strong className="text-white">Drug Test</strong> — You will also be required to complete a drug test. The Operations Manager or Coordinator will contact you via text or phone call to provide drug test instructions. The drug test must be completed within <strong className="text-white">48 hours</strong>. Please notify them once it has been completed.</li>
              <li><strong className="text-white">W-9 & Direct Deposit</strong> — After the drug test, you will receive the W-9 form and the Direct Deposit form to complete.</li>
              <li><strong className="text-white">Technician ID & Badge</strong> — You will then be assigned a technician number and a badge. Once your badge is ready, you will receive a call with information regarding your hotel and warehouse assignment.</li>
            </ol>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              variant="outline"
              onClick={() => navigate('/dashboard')}
              className="flex-1 border-slate-700 text-slate-300 hover:bg-slate-800"
              data-testid="decline-application"
            >
              Go Back
            </Button>
            <Button
              onClick={() => setAccepted(true)}
              className="flex-1 btn-gradient text-white"
              data-testid="accept-application"
            >
              I Understand, Continue
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0F]">
      {/* Header */}
      <header className="bg-[#12121A] border-b border-slate-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Logo />
            <Button 
              variant="ghost" 
              onClick={() => navigate('/dashboard')}
              className="text-slate-400 hover:text-white hover:bg-slate-800"
              data-testid="back-to-dashboard"
            >
              <ArrowLeft size={18} className="mr-2" />
              Dashboard
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Progress Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white mb-2" style={{ fontFamily: 'Syne, sans-serif' }}>
            Application Form
          </h1>
          <p className="text-slate-400">Complete all steps to submit your application</p>
          
          {/* Progress Bar */}
          <div className="mt-6">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-slate-400">Step {currentStep} of {STEPS.length}</span>
              <span className="font-semibold text-white">{getProgress()}%</span>
            </div>
            <Progress value={getProgress()} className="h-2" />
          </div>

          {/* Step Indicators */}
          <div className="flex justify-between mt-6">
            {STEPS.map((step) => {
              const Icon = step.icon;
              const isActive = currentStep === step.id;
              const isComplete = currentStep > step.id;
              
              return (
                <button
                  key={step.id}
                  onClick={() => setCurrentStep(step.id)}
                  className={`flex flex-col items-center gap-2 transition-colors ${stepTone(isActive, isComplete).text}`}
                  data-testid={`step-${step.id}`}
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center ${stepTone(isActive, isComplete).bg}`}>
                    {isComplete ? <CheckCircle size={20} /> : <Icon size={20} />}
                  </div>
                  <span className="text-xs font-medium hidden sm:block">{step.title}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Form Content */}
        <div className="bg-[#12121A] rounded-xl p-8 border border-slate-800 animate-fade-in">
          {/* Step 1: Personal Info */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>
                Personal Information
              </h2>
              
              <div className="grid sm:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label htmlFor="first_name" className="text-slate-300">First Name *</Label>
                  <Input
                    id="first_name"
                    value={formData.first_name}
                    onChange={(e) => handleChange('first_name', e.target.value)}
                    placeholder="John"
                    className={getInputClassName('first_name')}
                    data-testid="input-first-name"
                  />
                  {renderFieldError('first_name')}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="last_name" className="text-slate-300">Last Name *</Label>
                  <Input
                    id="last_name"
                    value={formData.last_name}
                    onChange={(e) => handleChange('last_name', e.target.value)}
                    placeholder="Doe"
                    className={getInputClassName('last_name')}
                    data-testid="input-last-name"
                  />
                  {renderFieldError('last_name')}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-300">Email Address *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  placeholder="you@example.com"
                  className={getInputClassName('email')}
                  data-testid="input-email"
                />
                {renderFieldError('email')}
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone" className="text-slate-300">Phone Number *</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  placeholder="(555) 123-4567"
                  className={getInputClassName('phone')}
                  data-testid="input-phone"
                />
                {renderFieldError('phone')}
              </div>

              {/* Market Selector */}
              {markets.length > 0 && (
                <div className="space-y-2">
                  <Label htmlFor="selected_market" className="text-slate-300">
                    <MapPin size={14} className="inline mr-1 text-violet-400" />
                    Available Market *
                  </Label>
                  <select
                    id="selected_market"
                    value={formData.selected_market}
                    onChange={(e) => handleChange('selected_market', e.target.value)}
                    className="w-full h-12 bg-[#0A0A0F] border border-slate-700 rounded-md text-white px-3 focus:border-violet-500 focus:outline-none focus:ring-1 focus:ring-violet-500/20"
                    data-testid="select-market"
                  >
                    <option value="" className="bg-[#0A0A0F]">Select a market...</option>
                    {markets.map(m => (
                      <option key={m.id} value={m.id} className="bg-[#0A0A0F]">{m.title}</option>
                    ))}
                  </select>
                  {renderFieldError('selected_market')}
                </div>
              )}
            </div>
          )}

          {/* Step 2: Identification */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>
                Identification Documents
              </h2>
              
              <div className="p-4 bg-violet-500/5 border border-violet-500/20 rounded-lg">
                <p className="text-sm text-slate-300 leading-relaxed">
                  Employment opportunities involving companies such as Spectrum, Comcast, and other organizations require a comprehensive background verification process. Applicants must provide a valid form of identification, proof of driving eligibility, and the necessary Social Security information. In addition, applicants must provide documentation confirming their legal authorization to work in the United States.
                </p>
              </div>

              {/* Yes/No Questions */}
              <div className="space-y-4">
                <div className="p-4 bg-[#0A0A0F] rounded-lg border border-slate-700">
                  <p className="text-sm font-medium text-white mb-3">Are you a legal U.S. citizen? *</p>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => handleChange('is_us_citizen', 'yes')}
                      className={`flex-1 py-2.5 rounded-lg text-sm font-medium border transition-colors ${
                        formData.is_us_citizen === 'yes'
                          ? 'bg-emerald-500/20 border-emerald-500/50 text-emerald-300'
                          : 'border-slate-700 text-slate-400 hover:border-slate-600'
                      }`}
                      data-testid="citizen-yes"
                    >
                      Yes
                    </button>
                    <button
                      type="button"
                      onClick={() => handleChange('is_us_citizen', 'no')}
                      className={`flex-1 py-2.5 rounded-lg text-sm font-medium border transition-colors ${
                        formData.is_us_citizen === 'no'
                          ? 'bg-red-500/20 border-red-500/50 text-red-300'
                          : 'border-slate-700 text-slate-400 hover:border-slate-600'
                      }`}
                      data-testid="citizen-no"
                    >
                      No
                    </button>
                  </div>
                </div>

                <div className="p-4 bg-[#0A0A0F] rounded-lg border border-slate-700">
                  <p className="text-sm font-medium text-white mb-3">Have you ever been convicted of a crime? *</p>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => handleChange('has_criminal_record', 'yes')}
                      className={`flex-1 py-2.5 rounded-lg text-sm font-medium border transition-colors ${
                        formData.has_criminal_record === 'yes'
                          ? 'bg-red-500/20 border-red-500/50 text-red-300'
                          : 'border-slate-700 text-slate-400 hover:border-slate-600'
                      }`}
                      data-testid="criminal-yes"
                    >
                      Yes
                    </button>
                    <button
                      type="button"
                      onClick={() => handleChange('has_criminal_record', 'no')}
                      className={`flex-1 py-2.5 rounded-lg text-sm font-medium border transition-colors ${
                        formData.has_criminal_record === 'no'
                          ? 'bg-emerald-500/20 border-emerald-500/50 text-emerald-300'
                          : 'border-slate-700 text-slate-400 hover:border-slate-600'
                      }`}
                      data-testid="criminal-no"
                    >
                      No
                    </button>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="social_security" className="text-slate-300">Social Security Number *</Label>
                <Input
                  id="social_security"
                  value={formData.social_security}
                  onChange={(e) => handleChange('social_security', e.target.value)}
                  placeholder="XXX-XX-XXXX"
                  className={getInputClassName('social_security')}
                  data-testid="input-ssn"
                />
                {renderFieldError('social_security')}
                <p className="text-xs text-slate-500">Your information is encrypted and secure</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="driver_license" className="text-slate-300">Driver's License Number *</Label>
                <Input
                  id="driver_license"
                  value={formData.driver_license}
                  onChange={(e) => handleChange('driver_license', e.target.value)}
                  placeholder="Enter your driver's license number"
                  className={getInputClassName('driver_license')}
                  data-testid="input-license"
                />
                {renderFieldError('driver_license')}
              </div>

              {/* Driver's License Photo Upload - REQUIRED */}
              <div className="space-y-4 pt-4 border-t border-slate-700">
                <h3 className="font-semibold text-white flex items-center gap-2">
                  <Camera size={16} className="text-violet-400" />
                  Driver's License Photo <span className="text-red-400 text-sm">*Required</span>
                </h3>
                
                <div 
                  className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors bg-[#0A0A0F] ${
                    hasDocType('Driver License') ? 'border-emerald-500/50' : 'border-slate-700 hover:border-violet-500/50'
                  }`}
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="upload-license-area"
                >
                  <Upload className="mx-auto text-slate-500 mb-2" size={28} />
                  <p className="text-slate-400 text-sm">Click to upload Driver's License photo</p>
                  <p className="text-xs text-slate-500 mt-1">PDF, JPG, PNG up to 10MB</p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    className="hidden"
                    onChange={(e) => handleFileUpload(e, 'Driver License')}
                    data-testid="file-upload-license"
                  />
                </div>

                {documents.filter(d => d.document_type === 'Driver License').length > 0 && (
                  <div className="space-y-2">
                    {documents.filter(d => d.document_type === 'Driver License').map(doc => (
                      <div key={doc.id} className="flex items-center gap-3 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                        <CheckCircle className="text-emerald-400" size={18} />
                        <span className="text-sm text-emerald-300">{doc.filename}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Social Security Photo Upload - REQUIRED */}
              <div className="space-y-4 pt-4 border-t border-slate-700">
                <h3 className="font-semibold text-white flex items-center gap-2">
                  <Camera size={16} className="text-violet-400" />
                  Social Security Card Photo <span className="text-red-400 text-sm">*Required</span>
                </h3>
                
                <div 
                  className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors bg-[#0A0A0F] ${
                    hasDocType('Social Security') ? 'border-emerald-500/50' : 'border-slate-700 hover:border-violet-500/50'
                  }`}
                  onClick={() => ssnFileRef.current?.click()}
                  data-testid="upload-ssn-area"
                >
                  <Upload className="mx-auto text-slate-500 mb-2" size={28} />
                  <p className="text-slate-400 text-sm">Click to upload Social Security Card photo</p>
                  <p className="text-xs text-slate-500 mt-1">PDF, JPG, PNG up to 10MB</p>
                  <input
                    ref={ssnFileRef}
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    className="hidden"
                    onChange={(e) => handleFileUpload(e, 'Social Security')}
                    data-testid="file-upload-ssn"
                  />
                </div>

                {documents.filter(d => d.document_type === 'Social Security').length > 0 && (
                  <div className="space-y-2">
                    {documents.filter(d => d.document_type === 'Social Security').map(doc => (
                      <div key={doc.id} className="flex items-center gap-3 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                        <CheckCircle className="text-emerald-400" size={18} />
                        <span className="text-sm text-emerald-300">{doc.filename}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Validation warning */}
              {!canProceedFromStep2() && (
                <div className="flex items-center gap-2 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                  <AlertCircle className="text-amber-400 flex-shrink-0" size={18} />
                  <p className="text-sm text-amber-300">Both Driver's License and Social Security photos are required to continue.</p>
                </div>
              )}
            </div>
          )}

          {/* Step 3: Equipment */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>
                Equipment & Resources
              </h2>
              
              <div className="space-y-4">
                {/* Truck */}
                <div className="flex items-start gap-4 p-4 bg-[#0A0A0F] rounded-lg border border-slate-700">
                  <Checkbox
                    id="has_truck"
                    checked={formData.has_truck}
                    onCheckedChange={(checked) => handleChange('has_truck', checked)}
                    data-testid="checkbox-truck"
                  />
                  <div className="flex-1">
                    <Label htmlFor="has_truck" className="cursor-pointer font-semibold text-white">
                      I have my own truck
                    </Label>
                    {formData.has_truck && (
                      <div className="mt-3 space-y-3">
                        <Input
                          value={formData.truck_details}
                          onChange={(e) => handleChange('truck_details', e.target.value)}
                          placeholder="Year, make, model (e.g., 2020 Ford F-150)"
                          className={getInputClassName('truck_details')}
                          data-testid="input-truck-details"
                        />
                        {renderFieldError('truck_details')}
                      </div>
                    )}
                  </div>
                </div>

                {/* Rent Vehicle */}
                <div className="flex items-start gap-4 p-4 bg-[#0A0A0F] rounded-lg border border-slate-700">
                  <Checkbox
                    id="needs_rent_vehicle"
                    checked={formData.needs_rent_vehicle}
                    onCheckedChange={(checked) => handleChange('needs_rent_vehicle', checked)}
                    data-testid="checkbox-rent-vehicle"
                  />
                  <div className="flex-1">
                    <Label htmlFor="needs_rent_vehicle" className="cursor-pointer font-semibold text-white">
                      Do you need to rent a vehicle?
                    </Label>
                  </div>
                </div>

                {/* Vehicle Photo Upload */}
                <div className="p-4 bg-[#0A0A0F] rounded-lg border border-slate-700">
                  <h3 className="font-semibold text-white flex items-center gap-2 mb-1">
                    <Camera size={16} className="text-violet-400" />
                    Vehicle Photo {!formData.needs_rent_vehicle && <span className="text-red-400 text-sm">*Required</span>}
                  </h3>
                  <p className="text-xs text-slate-400 mb-3">
                    Vehicle Photos Required: Please submit clear photos of your vehicle showing the front, rear, both sides, and license plate.
                  </p>
                  <div 
                    className={`border-2 border-dashed rounded-lg p-5 text-center cursor-pointer transition-colors ${
                      hasDocType('Vehicle Photo') ? 'border-emerald-500/50 bg-emerald-500/5' : 'border-slate-600 hover:border-violet-500/50'
                    }`}
                    onClick={() => vehicleFileRef.current?.click()}
                    data-testid="upload-vehicle-area"
                  >
                    <Upload className="mx-auto text-slate-500 mb-1" size={24} />
                    <p className="text-slate-400 text-sm">Upload a photo of your vehicle</p>
                    <input
                      ref={vehicleFileRef}
                      type="file"
                      accept=".jpg,.jpeg,.png"
                      className="hidden"
                      onChange={(e) => handleFileUpload(e, 'Vehicle Photo')}
                      data-testid="file-upload-vehicle"
                    />
                  </div>
                  {documents.filter(d => d.document_type === 'Vehicle Photo').map(doc => (
                    <div key={doc.id} className="flex items-center gap-3 p-2 mt-2 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                      <CheckCircle className="text-emerald-400" size={16} />
                      <span className="text-sm text-emerald-300">{doc.filename}</span>
                    </div>
                  ))}
                </div>

                {/* Tools */}
                <div className="flex items-start gap-4 p-4 bg-[#0A0A0F] rounded-lg border border-slate-700">
                  <Checkbox
                    id="has_tools"
                    checked={formData.has_tools}
                    onCheckedChange={(checked) => handleChange('has_tools', checked)}
                    data-testid="checkbox-tools"
                  />
                  <div className="flex-1">
                    <Label htmlFor="has_tools" className="cursor-pointer font-semibold text-white">
                      I have my own tools
                    </Label>
                    {formData.has_tools && (
                      <div className="mt-3">
                        <Textarea
                          value={formData.tools_details}
                          onChange={(e) => handleChange('tools_details', e.target.value)}
                          placeholder="List your main tools..."
                          className="bg-[#0A0A0F] border-slate-700 text-white placeholder:text-slate-500 focus:border-violet-500"
                          rows={3}
                          data-testid="input-tools-details"
                        />
                        {renderFieldError('tools_details')}
                      </div>
                    )}
                  </div>
                </div>

                {/* Tools Photo Upload */}
                <div className="p-4 bg-[#0A0A0F] rounded-lg border border-slate-700">
                  <h3 className="font-semibold text-white flex items-center gap-2 mb-1">
                    <Camera size={16} className="text-violet-400" />
                    Tools Photo <span className="text-red-400 text-sm">*Required</span>
                  </h3>
                  <p className="text-xs text-slate-400 mb-3">
                    Please provide one photo showing all the tools you have available.
                  </p>
                  <div 
                    className={`border-2 border-dashed rounded-lg p-5 text-center cursor-pointer transition-colors ${
                      hasDocType('Tools Photo') ? 'border-emerald-500/50 bg-emerald-500/5' : 'border-slate-600 hover:border-violet-500/50'
                    }`}
                    onClick={() => toolsFileRef.current?.click()}
                    data-testid="upload-tools-area"
                  >
                    <Upload className="mx-auto text-slate-500 mb-1" size={24} />
                    <p className="text-slate-400 text-sm">Upload a photo of your tools</p>
                    <input
                      ref={toolsFileRef}
                      type="file"
                      accept=".jpg,.jpeg,.png"
                      className="hidden"
                      onChange={(e) => handleFileUpload(e, 'Tools Photo')}
                      data-testid="file-upload-tools"
                    />
                  </div>
                  {documents.filter(d => d.document_type === 'Tools Photo').map(doc => (
                    <div key={doc.id} className="flex items-center gap-3 p-2 mt-2 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                      <CheckCircle className="text-emerald-400" size={16} />
                      <span className="text-sm text-emerald-300">{doc.filename}</span>
                    </div>
                  ))}
                </div>

                {/* Meter Equipment - two side by side checkboxes */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="flex items-start gap-4 p-4 bg-[#0A0A0F] rounded-lg border border-slate-700">
                    <Checkbox
                      id="has_meter"
                      checked={formData.has_meter}
                      onCheckedChange={(checked) => handleChange('has_meter', checked)}
                      data-testid="checkbox-meter"
                    />
                    <div className="flex-1">
                      <Label htmlFor="has_meter" className="cursor-pointer font-semibold text-white">
                        I have testing/meter equipment
                      </Label>
                      {formData.has_meter && (
                        <div className="mt-3">
                          <Input
                            value={formData.meter_details}
                            onChange={(e) => handleChange('meter_details', e.target.value)}
                            placeholder="Type of meter/testing equipment"
                            className={getInputClassName('meter_details')}
                            data-testid="input-meter-details"
                          />
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-start gap-4 p-4 bg-[#0A0A0F] rounded-lg border border-slate-700">
                    <Checkbox
                      id="needs_rent_meter"
                      checked={formData.needs_rent_meter}
                      onCheckedChange={(checked) => handleChange('needs_rent_meter', checked)}
                      data-testid="checkbox-rent-meter"
                    />
                    <div className="flex-1">
                      <Label htmlFor="needs_rent_meter" className="cursor-pointer font-semibold text-white">
                        I need to rent a meter
                      </Label>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Experience */}
          {currentStep === 4 && (
            <div className="space-y-6">
              <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>
                Experience & Certifications
              </h2>
              
              <div className="space-y-2">
                <Label htmlFor="work_experience" className="text-slate-300">Work Experience *</Label>
                <Textarea
                  id="work_experience"
                  value={formData.work_experience}
                  onChange={(e) => handleChange('work_experience', e.target.value)}
                  placeholder="Describe your relevant work experience in telecommunications, electrical, or technical fields..."
                  className={`bg-[#0A0A0F] border-slate-700 text-white placeholder:text-slate-500 focus:border-violet-500 ${fieldErrors.work_experience ? 'border-red-500 bg-red-500/10' : ''}`}
                  rows={5}
                  data-testid="input-experience"
                />
                {renderFieldError('work_experience')}
              </div>

              <div className="space-y-2">
                <Label htmlFor="certifications" className="text-slate-300">Certifications & Licenses</Label>
                <Textarea
                  id="certifications"
                  value={formData.certifications}
                  onChange={(e) => handleChange('certifications', e.target.value)}
                  placeholder="List any relevant certifications (e.g., OSHA, CDL, fiber optic certifications)..."
                  className={`bg-[#0A0A0F] border-slate-700 text-white placeholder:text-slate-500 focus:border-violet-500 ${fieldErrors.certifications ? 'border-red-500 bg-red-500/10' : ''}`}
                  rows={4}
                  data-testid="input-certifications"
                />
                {renderFieldError('certifications')}
              </div>

              {/* Certification Document Upload */}
              <div className="space-y-4 pt-4 border-t border-slate-700">
                <h3 className="font-semibold text-white">Upload Certifications (Optional)</h3>
                
                <div 
                  className="border-2 border-dashed border-slate-700 rounded-lg p-8 text-center cursor-pointer hover:border-violet-500/50 transition-colors bg-[#0A0A0F]"
                  onClick={() => document.getElementById('cert-upload')?.click()}
                >
                  <Upload className="mx-auto text-slate-500 mb-2" size={32} />
                  <p className="text-slate-400">Click to upload certification documents</p>
                  <p className="text-xs text-slate-500 mt-1">PDF, JPG, PNG up to 10MB</p>
                  <input
                    id="cert-upload"
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    className="hidden"
                    onChange={(e) => handleFileUpload(e, 'Certification')}
                  />
                </div>

                {documents.filter(d => d.document_type === 'Certification').length > 0 && (
                  <div className="space-y-2">
                    {documents.filter(d => d.document_type === 'Certification').map(doc => (
                      <div key={doc.id} className="flex items-center gap-3 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                        <CheckCircle className="text-emerald-400" size={18} />
                        <span className="text-sm text-emerald-300">{doc.filename}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-700">
            <div>
              {currentStep > 1 && (
                <Button
                  variant="outline"
                  onClick={() => setCurrentStep(currentStep - 1)}
                  className="border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white"
                  data-testid="prev-step-btn"
                >
                  <ArrowLeft size={18} className="mr-2" />
                  Previous
                </Button>
              )}
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => saveProgress(true)}
                disabled={saving}
                className="border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white"
                data-testid="save-progress-btn"
              >
                {saving ? <Loader2 className="animate-spin mr-2" size={18} /> : <Save size={18} className="mr-2" />}
                Save Progress
              </Button>

              {currentStep < STEPS.length ? (
                <Button
                  onClick={() => {
                    if (currentStep === 2 && !canProceedFromStep2()) {
                      toast.error('Please upload both Driver\'s License and Social Security photos to continue.');
                      return;
                    }
                    saveProgress(true);
                    setCurrentStep(currentStep + 1);
                  }}
                  className="btn-gradient text-white"
                  data-testid="next-step-btn"
                >
                  Next
                  <ArrowRight size={18} className="ml-2" />
                </Button>
              ) : (
                <Button
                  onClick={submitApplication}
                  disabled={submitting}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="submit-application-btn"
                >
                  {submitting ? (
                    <Loader2 className="animate-spin mr-2" size={18} />
                  ) : (
                    <Send size={18} className="mr-2" />
                  )}
                  Submit Application
                </Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
